import React, { Fragment, useMemo, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import classnames from "classnames";

import { Banner, IconButton } from "fe-shared/src/components";
import { EditIcon } from "fe-shared/src/components/Icons";

import useDialog from "fe-shared/src/hooks/useDialog";
import { getOffsetDate } from "fe-shared/src/helpers/dates";
import { handleKeyDown } from "fe-shared/src/helpers/buttonHandlers";
import {
  MaterialGroup,
  MaterialGroupByRegYear,
  MaterialUnit,
} from "fe-shared/src/types/domain";

import {
  DetailsInfoBlock,
  DetailsInfoTitle,
  DetailsModal,
  DocumentsInfo,
  EditTransferDialog,
  InactiveStatus,
} from "..";
import getMaterialGroups from "../../helpers/materialsConverter";
import getMaterialGroupsByRegYear from "../../helpers/getMaterialGroupsByRegYear";
import { getFullAddress } from "../../helpers/address";
import { getMaterialTotals } from "../../helpers/transactions";
import {
  InfoRow,
  TransactionMaterial,
  TransactionTraversedMaterial,
  TransactionWarehouseLocation,
  Transfer,
  TransferStatus,
  TransferType,
} from "../../types";

import styles from "./TransferContentSection.module.scss";

export interface Props {
  unit: MaterialUnit;
  materialName?: string;
  location?: TransactionWarehouseLocation;
  transfer: Transfer;
  canEditTransfer?: boolean;
  isWithDocuments: boolean;
  isWithNonEligibles?: boolean;
  isActive?: boolean;
  isStaffView?: boolean;
  showComparisonMaterials?: boolean;
}

const TransferContentSection = ({
  unit,
  location,
  transfer,
  materialName,
  canEditTransfer,
  isWithDocuments,
  isActive = true,
  isWithNonEligibles = false,
  isStaffView,
  showComparisonMaterials,
}: Props) => {
  const {
    comment,
    materials,
    traversedMaterials,
    nonEligibleMaterial,
    scaleTickets,
    paperFormImages,
    materialPhotos,
    statusCode,
    isCorrected,
    transferType,
    isAdjustable,
  } = transfer;
  const isEditable =
    canEditTransfer &&
    statusCode !== TransferStatus.Invalid &&
    statusCode !== TransferStatus.Invoiced &&
    (isStaffView || isWithDocuments) &&
    (isStaffView || isAdjustable);
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [wasWarningDismissed, setWasWarningDismissed] = useState(false);
  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();
  const [isPhotoDialog, setIsPhotoDialog] = useState(true);

  const photos = useMemo(
    () => [...(paperFormImages || []), ...materialPhotos],
    [paperFormImages, materialPhotos]
  );

  const isRegYearEnabled = useMemo(
    () =>
      [TransferType.IntoProcessing, TransferType.OutOfProcessing].includes(
        transferType
      ) &&
      !!traversedMaterials &&
      !!traversedMaterials.length,
    [transferType, traversedMaterials]
  );

  const materialTotals = useMemo(() => getMaterialTotals(materials), [
    materials,
  ]);

  const materialsInfo: InfoRow[] = useMemo(
    () =>
      getMaterialGroups(materials)
        .map(
          (g: MaterialGroup<TransactionMaterial>) =>
            [
              {
                key: g.materialCategoryName,
                label: `${t(
                  `transfers.details.materialTypes.${g.materialCategoryName}`
                )}, ${t(`transfers.details.units.${unit}`)}`,
                value: (
                  materialTotals[g.materialCategoryName] || 0
                ).toLocaleString(),
                type: "title",
              },
              ...g.items.map((i: TransactionMaterial) => ({
                key: i.materialCode,
                label: i.materialName,
                value: (i.units.unitQuantity || 0).toLocaleString(),
                type: "row",
              })),
            ] as InfoRow[]
        )
        .flat(),
    [t, unit, materials, materialTotals]
  );

  const nonEligibleMaterialInfo: InfoRow[] = useMemo(() => {
    if (!isWithNonEligibles || !nonEligibleMaterial) return [];

    const {
      materialCategoryName,
      units,
      materialName: label,
      materialCode,
    } = nonEligibleMaterial;

    return [
      {
        key: materialCategoryName,
        label: `${t(
          `transfers.details.materialTypes.${materialCategoryName}`
        )}, ${t(`transfers.details.units.${unit}`)}`,
        value: units.unitQuantity.toLocaleString(),
        type: "title",
      },
      {
        key: materialCode,
        label,
        value: (units.unitQuantity || 0).toLocaleString(),
        type: "row",
      },
    ] as InfoRow[];
  }, [isWithNonEligibles, nonEligibleMaterial, t, unit]);

  const traversedMaterialsByRegYear = useMemo(
    () => getMaterialGroupsByRegYear(traversedMaterials),
    [traversedMaterials]
  );

  const rowsInfo = useMemo(
    () => (
      <DetailsInfoBlock
        rowClassName={classnames(styles.titleRowPadding, {
          [styles.deprecated]: isRegYearEnabled,
        })}
        rows={materialsInfo}
      />
    ),
    [isRegYearEnabled, materialsInfo]
  );

  const rowsByRegYearInfo = useMemo(
    () =>
      traversedMaterialsByRegYear.map(
        (
          groupsByRegYear: MaterialGroupByRegYear<TransactionTraversedMaterial>
        ) => {
          const info: InfoRow[] = groupsByRegYear.materialGroups
            .map(
              (g: MaterialGroup<TransactionMaterial>) =>
                [
                  {
                    key: g.materialCategoryName,
                    label: `${t(
                      `transfers.details.materialTypes.${g.materialCategoryName}`
                    )}, ${t(`transfers.details.units.${g.unit}`)}`,
                    value: (g.total || 0).toLocaleString(),
                    type: "title",
                  },
                  ...g.items.map((i: TransactionMaterial) => ({
                    key: i.materialCode,
                    label: i.materialName,
                    value: (i.units.unitQuantity || 0).toLocaleString(),
                    type: "row",
                  })),
                ] as InfoRow[]
            )
            .flat();

          return (
            <Fragment key={`${groupsByRegYear.regYear}-group-reg-year`}>
              <p className={styles.regYearTitle}>
                {groupsByRegYear.regYear
                  ? t("transfers.details.regYear", {
                      regYear: groupsByRegYear.regYear,
                    })
                  : t("transfers.details.unidentifiedRegYear")}
              </p>

              <DetailsInfoBlock
                rowClassName={styles.titleRowPadding}
                rows={info}
              />
            </Fragment>
          );
        }
      ),
    [t, traversedMaterialsByRegYear]
  );

  const rowsNonEligibleInfo = useMemo(
    () => (
      <DetailsInfoBlock
        rowClassName={styles.titleRowPadding}
        rows={nonEligibleMaterialInfo}
      />
    ),
    [nonEligibleMaterialInfo]
  );

  const onCloseDialog = () => {
    setSelectedImage(null);
    closeDialog();
  };

  return (
    <>
      <div className={styles.sectionActionContainer}>
        <div className={styles.transactionDataContainer}>
          <div className={styles.tiresInformation}>
            <DetailsInfoTitle
              title={t(`transfers.details.dataTitle.${transferType}`, {
                materialName,
              })}
              subTitle={getFullAddress(location?.address)}
              inactiveLine={!isActive && <InactiveStatus />}
            />
            {isEditable && (
              <IconButton
                hasTooltip
                id="edit-transfer-button"
                aria-label={t("transfers.actions.edit")}
                className={styles.editButton}
                onClick={() => {
                  openDialog();
                  setIsPhotoDialog(false);
                }}
              >
                <EditIcon fillColor={null} />
              </IconButton>
            )}
            {(!isRegYearEnabled || showComparisonMaterials) && rowsInfo}
            {isRegYearEnabled && rowsByRegYearInfo}
            {isWithNonEligibles && nonEligibleMaterial && (
              <section>{rowsNonEligibleInfo}</section>
            )}
            {!!photos.length && (
              <div className={styles.tiresPicWrapper}>
                {photos.map((item) => {
                  const onClick = () => {
                    setSelectedImage(item);
                    setIsPhotoDialog(true);
                    openDialog();
                  };

                  return (
                    <div
                      key={item}
                      role="button"
                      tabIndex={0}
                      className={styles.tirePic}
                      onClick={onClick}
                      onKeyDown={(e) => handleKeyDown(e, onClick)}
                    >
                      <img src={item} alt="" />
                    </div>
                  );
                })}
              </div>
            )}
            {!!comment && <p>{comment}</p>}
          </div>
        </div>
      </div>
      {isWithDocuments && (
        <DocumentsInfo
          isEditable={canEditTransfer}
          address={location?.address}
          attachments={transfer.attachments}
          transfer={transfer}
          title={t("transactions.details.attachments.title")}
        />
      )}
      {!!scaleTickets?.length && (
        <section>
          {scaleTickets.map((st) => (
            <Fragment key={st.ticketNumber}>
              <div className={styles.scaleTicketRow}>
                <span>{t("transfers.details.scaleTicketDate")}</span>
                <span>
                  {t(
                    format(
                      getOffsetDate(new Date(st.createdDate))!,
                      "MMM dd, yyyy"
                    )
                  )}
                </span>
              </div>
              <div className={styles.scaleTicketRow}>
                <span>{t("transfers.details.scaleTicketNumber")}</span>
                <span>{st.ticketNumber}</span>
              </div>
            </Fragment>
          ))}
        </section>
      )}
      {!wasWarningDismissed &&
        isCorrected &&
        isStaffView &&
        transferType !== TransferType.NonEligibleWriteOff && (
          <Banner
            variant="warning"
            isScrolledIntoView={false}
            title={t("transactions.warning.title")}
            id="transfer-load-changed-warning-banner"
            onDismiss={() => {
              setWasWarningDismissed(true);
            }}
          >
            {t("transactions.warning.description", {
              type: t("transactions.warning.transfer"),
              action: t("transactions.warning.corrected"),
            })}
          </Banner>
        )}
      {isDialogRendered && isPhotoDialog && (
        <DetailsModal
          selectedImage={selectedImage}
          isDialogActive={isDialogActive}
          onCloseDialog={onCloseDialog}
          materialsInfo={materialsInfo}
          transactionOrTransfer={transfer}
        />
      )}
      {isDialogRendered && !isPhotoDialog && (
        <EditTransferDialog
          address={location?.address}
          closeDialog={closeDialog}
          transfer={transfer}
          isDialogActive={isDialogActive}
          isStaffView={isStaffView}
          isWithDocuments={isWithDocuments}
        />
      )}
    </>
  );
};

export default TransferContentSection;
